import { MolecularGraph } from "../graph/MolecularGraph";
import { Feature, Features } from "../feature";

/**
 * PIPH-116: Reclassify terminal methyl groups as functional groups.
 *
 * A 1-carbon chain (methyl) should become a functional group when:
 * 1. It has exactly ONE connection to another primary feature
 * 2. It has NO functional groups attached to it
 *
 * It should REMAIN a primary feature (1-carbon chain) when:
 * 1. It has TWO OR MORE connections to other primaries (bridging)
 * 2. It has its own functional groups (e.g., alcohol on the carbon)
 */
export const methylReclassifyFilter = {
    filterInPlace: (features: Feature[], _graph: MolecularGraph) => {
        const methylFeatures = features.filter(f => f.name === Features.methyl);

        // Get all primary structural features (rings, chains, bridged rings, atom clusters)
        // Primary features have names starting with: ring, chain, ringSystem, atomCluster
        const primaryFeatures = features.filter(f => {
            const name = f.name;
            return (
                name.startsWith(Features.ring) ||
                name.startsWith(Features.chain) ||
                name.startsWith(Features.ringSystem) ||
                name === Features.atomCluster ||
                name.startsWith(Features.bridgedRing)
            );
        });

        // Get all functional groups (features that are not primaries and not methyls we're evaluating)
        const functionalGroups = features.filter(f => {
            const name = f.name;
            const isPrimary = (
                name.startsWith(Features.ring) ||
                name.startsWith(Features.chain) ||
                name.startsWith(Features.ringSystem) ||
                name === Features.atomCluster ||
                name.startsWith(Features.bridgedRing)
            );
            return !isPrimary;
        });

        for (const methylFeature of methylFeatures) {
            const methylAtom = methylFeature.atoms[0]; // Methyl has a single carbon

            // Count connections to OTHER primary features
            // A methyl's connections are the atoms it bonds to outside itself
            const connectedPrimaries = primaryFeatures.filter(primary => {
                if (primary === methylFeature) return false;
                // Check if methyl's connection atoms touch this primary
                return methylFeature.connections.some(connAtom =>
                    primary.atoms.includes(connAtom)
                );
            });

            // Check if methyl has any functional groups attached to it
            // A functional group is attached if its connection atoms include the methyl carbon
            const attachedFunctionalGroups = functionalGroups.filter(fg => {
                return fg.connections.includes(methylAtom);
            });

            // PIPH-116 reclassification criteria:
            // - Exactly ONE connection to another primary
            // - NO functional groups attached
            const shouldReclassify =
                connectedPrimaries.length === 1 &&
                attachedFunctionalGroups.length === 0;

            if (shouldReclassify) {
                const parent = connectedPrimaries[0];

                // Set parent relationship so classification and serialization treat it as functional group
                methylFeature.parent = parent;

                // Add to parent's children if not already there
                if (!parent.children) {
                    parent.children = [];
                }
                if (!parent.children.includes(methylFeature)) {
                    parent.children.push(methylFeature);
                }
            }
        }

        return features;
    }
};
